/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate } from 'react-router-dom';
import Pagination from "react-js-pagination";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import { formatUTCDate } from "../../utils/utils";
import ExportButton from "../active-users/ExportButton";
import DateRangeBar, { fmt, presetRange } from "../active-users/DateRangeBar";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import UserDetailLink from "../common/UserDetailLink";
import UserSegmentToggle from "../common/UserSegmentToggle";
import { useUserSegment, watchFilterValue } from "../../context/UserSegmentContext";
import { readSavedFilters, usePersistFilters } from "../../hooks/filterPersistence";
import "../active-users/active-users.css";
import "./WorkoutData.css";

/**
 * /workout-records (147) — every workout record across all users, latest Timestamp UTC first.
 * Same record columns as /workout-details plus the member's Country / App Version /
 * Subscription. Filters: name/email search, subscription, Workout Type, Workout Type Raw,
 * Source Name (dropdowns of the unique DB values) and a Timestamp UTC date range, plus the
 * global Watch toggle (top right, shared with every list page).
 */

const LIMIT = 20;
export const STATE_KEY = 'workoutRecordListState';

const EMPTY_FILTERS = { search: '', subscription: '', workoutType: '', workoutTypeRaw: '', sourceName: '' };
const DEFAULT_SORT = { field: 'timestamp_utc', order: 'DESC' };

// Milliseconds → h/m/s, same as the Workout Details page.
export const formatDuration = (ms) => {
  if (ms == null) return '-';
  const totalSeconds = Math.floor(Number(ms) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
  return parts.join(' ');
};

const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'country', label: 'Country' },
  { key: 'mobile_app_version', label: 'App Version' },
  { key: 'subscription_status', label: 'Subscription' },
  { key: 'workout_type', label: 'Workout Type' },
  { key: 'workout_type_raw', label: 'Workout Type Raw' },
  { key: 'source_name', label: 'Source Name' },
  { key: 'duration', label: 'Duration' },
  { key: 'start_time', label: 'Start Time' },
  { key: 'end_time', label: 'End Time' },
  { key: 'timestamp_utc', label: 'Timestamp UTC' },
];

const WorkoutRecordList = () => {
  const navigate = useNavigate();
  const { segment } = useUserSegment();                      // global watch/without/all toggle
  const userSegment = watchFilterValue(segment) || 'all';    // 'with_watch' | 'without_watch' | 'all'
  const saved = readSavedFilters(STATE_KEY);

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [records, setRecords] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [page, setPage] = useState(saved?.page || 1);

  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, ...(saved?.filters || {}) });
  const [fromDate, setFromDate] = useState(saved?.fromDate || '');
  const [toDate, setToDate] = useState(saved?.toDate || '');
  const [displayFromDate, setDisplayFromDate] = useState(saved?.fromDate ? new Date(saved.fromDate) : null);
  const [displayToDate, setDisplayToDate] = useState(saved?.toDate ? new Date(saved.toDate) : null);
  const [preset, setPreset] = useState(saved?.preset || null);
  const [sort, setSort] = useState(saved?.sort || DEFAULT_SORT);

  const [options, setOptions] = useState({ workout_types: [], workout_type_raws: [], source_names: [] });

  usePersistFilters(STATE_KEY, { page, filters, fromDate, toDate, preset, sort });

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => { refreshToken(); }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    if (!token) return;
    getList({ p: page });
    axiosJWT.get(`${baseUrl}/admin/api/workout-records-filters`, auth())
      .then((res) => setOptions({ workout_types: [], workout_type_raws: [], source_names: [], ...(res.data.data || {}) }))
      .catch(() => { /* dropdowns stay empty; the list still works */ });
  }, [token]);

  // Re-fetch from page 1 when the global Watch toggle changes (skip the initial mount).
  const didMountSegment = useRef(false);
  useEffect(() => {
    if (!didMountSegment.current) { didMountSegment.current = true; return; }
    if (token) reload();
  }, [segment]);

  // One query string for list + export, so the CSV always matches the table.
  const filterQs = ({ f = filters, from = fromDate, to = toDate, s = sort } = {}) => {
    const qp = new URLSearchParams({ from_date: from, to_date: to, sortField: s.field, sortOrder: s.order, user_segment: userSegment });
    if (f.search.trim()) qp.set('search', f.search.trim());
    if (f.subscription) qp.set('subscription_status', f.subscription);
    if (f.workoutType) qp.set('workout_type', f.workoutType);
    if (f.workoutTypeRaw) qp.set('workout_type_raw', f.workoutTypeRaw);
    if (f.sourceName) qp.set('source_name', f.sourceName);
    return qp.toString();
  };

  const getList = async ({ p = 1, ...args } = {}) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/workout-records?page=${p}&limit=${LIMIT}&${filterQs(args)}`, auth());
      setRecords(res.data.data || []);
      setTotalItem(res.data.totalItem || 0);
    } catch (err) {
      setError(err?.response?.status === 401 ? 'auth' : 'load');
    } finally { setLoading(false); }
  };

  const reload = (args = {}) => { setPage(1); getList({ p: 1, ...args }); };

  const handlePageChange = (p) => { setPage(p); getList({ p }); };
  const setText = (e) => setFilters({ ...filters, search: e.target.value });
  // Dropdowns apply straight away; the text search applies on Search / Enter.
  const setSelect = (key) => (e) => {
    const next = { ...filters, [key]: e.target.value };
    setFilters(next);
    reload({ f: next });
  };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    const { fromStr: fs, toStr: ts } = presetRange(key, new Date());
    const from = fromStr || fs; const to = toStr || ts;
    setPreset(key); setFromDate(from); setToDate(to);
    setDisplayFromDate(new Date(from)); setDisplayToDate(new Date(to));
    reload({ from, to });
  };
  const handleSearch = () => reload();
  const clearFilter = () => {
    setFilters(EMPTY_FILTERS); setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null);
    setPreset(null); setSort(DEFAULT_SORT);
    reload({ f: EMPTY_FILTERS, from: '', to: '', s: DEFAULT_SORT });
  };
  const handleSort = (field) => {
    const next = { field, order: sort.field === field && sort.order === 'DESC' ? 'ASC' : 'DESC' };
    setSort(next);
    reload({ s: next });
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(`${baseUrl}/admin/api/workout-records-export?${filterQs()}`, auth());
    return (res.data.data || []).map((r) => ({ ...r, duration: formatDuration(r.duration) }));
  };

  const SortTh = ({ field, label }) => (
    <th className="au-th-sort" onClick={() => handleSort(field)}>
      {label} {sort.field === field ? (sort.order === 'ASC' ? '🔼' : '🔽') : ''}
    </th>
  );
  const hasFilters = !!(fromDate || toDate || Object.values(filters).some(Boolean));
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };

  const renderSelect = (name, label, all, values) => (
    <select className="au-select" name={name} value={filters[name]} onChange={setSelect(name)} aria-label={label}>
      <option value="">{all}</option>
      {/* Keep a saved value selectable even before / without the options load. */}
      {filters[name] && !values.includes(filters[name]) && <option value={filters[name]}>{filters[name]}</option>}
      {values.map((v) => <option key={v} value={v}>{v}</option>)}
    </select>
  );

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Workouts › <b>All records</b></span>
            <span className="au-route">/workout-records</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
          </div>
          <h1 className="au-title">Workout Records</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : (
            <>
              <DateRangeBar
                leading={(
                  <>
                    <input className="au-input" type="text" placeholder="Search by name or email" value={filters.search}
                      onChange={setText} onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }} aria-label="Search users" />
                    <select className="au-select" name="subscription" value={filters.subscription} onChange={setSelect('subscription')} aria-label="Subscription filter">
                      <option value="">All Subscriptions</option>
                      <option value="1">Active</option>
                      <option value="2">Cancel</option>
                      <option value="4">Expired</option>
                    </select>
                    {renderSelect('workoutType', 'Workout Type filter', 'All Workout Types', options.workout_types)}
                    {renderSelect('workoutTypeRaw', 'Workout Type Raw filter', 'All Workout Type Raw', options.workout_type_raws)}
                    {renderSelect('sourceName', 'Source Name filter', 'All Source Names', options.source_names)}
                  </>
                )}
                preset={preset}
                displayFrom={displayFromDate}
                displayTo={displayToDate}
                hasRange={hasFilters}
                onPreset={handlePreset}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                onSearch={handleSearch}
                onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="wk-records-range-note">
                The date range filters workouts by their <b>record date (UTC)</b> — the value shown as “Timestamp UTC”.{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all workouts (no date filter).'}
              </p>

              {error === 'load' ? (
                <ErrorState message="We couldn’t load the workout records. Please try again." onRetry={() => getList({ p: page })} />
              ) : loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : records.length === 0 ? (
                <EmptyState title="No workout records" hasFilters={hasFilters} onClear={clearFilter}
                  message={hasFilters ? 'No workout records match the current filters.' : 'There are no workout records to show yet.'} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Workout records <span className="wk-records__total" data-testid="wk-records-total">({totalItem.toLocaleString()})</span></h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="workout-records.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table wk-table" data-testid="wk-records-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Email</th><th>Country</th><th>App Version</th><th>Subscription</th>
                          <th>Workout Type</th><th>Workout Type Raw</th><th>Source Name</th>
                          <SortTh field="duration" label="Duration" />
                          <SortTh field="start_time" label="Start Time" />
                          <SortTh field="end_time" label="End Time" />
                          <SortTh field="timestamp_utc" label="Timestamp UTC" />
                        </tr>
                      </thead>
                      <tbody>
                        {records.map((row, index) => (
                          <tr key={row.id ?? index}>
                            <td data-label="No">{(page - 1) * LIMIT + index + 1}</td>
                            <td data-label="Name" className="au-ellipsis"><UserDetailLink userId={row.user_id}>{row.name}</UserDetailLink></td>
                            <td data-label="Email" className="au-ellipsis"><UserDetailLink userId={row.user_id}>{row.email}</UserDetailLink></td>
                            <td data-label="Country">{row.country || '-'}</td>
                            <td data-label="App Version">{row.mobile_app_version || '-'}</td>
                            <td data-label="Subscription">{row.subscription_status || '-'}</td>
                            <td data-label="Workout Type">{row.workout_type || '-'}</td>
                            <td data-label="Workout Type Raw">{row.workout_type_raw || '-'}</td>
                            <td data-label="Source Name">{row.source_name || '-'}</td>
                            <td data-label="Duration" className="tnum">{formatDuration(row.duration)}</td>
                            <td data-label="Start Time">{row.start_time ? formatUTCDate(row.start_time) : '-'}</td>
                            <td data-label="End Time">{row.end_time ? formatUTCDate(row.end_time) : '-'}</td>
                            <td data-label="Timestamp UTC">{row.timestamp_utc ? formatUTCDate(row.timestamp_utc) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkoutRecordList;
